import { useEffect } from "react";

const SITE = "taskeri®";
const DEFAULT_TITLE = `${SITE} - On-Demand Gig Platform | Verified Task Heroes`;
const DEFAULT_DESC =
  "taskeri® connects you with verified Task Heroes for home services, cleaning, moving, deliveries & more across Canada. Book trusted local help in minutes.";
const BASE_URL = "https://taskeri.co";
const OG_IMAGE = `${BASE_URL}/og-image.png?v=2`;
const IMAGE_ALT = "taskeri® — verified Task Heroes, booked in minutes";
const INDEXABLE = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

// react-helmet-async 2.x does not apply meta/link tags under React 19 (only the
// title landed), which left every page sharing the homepage description and
// canonical. Updating the existing head tags in place keeps exactly one of each.
const upsert = (tag, selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(tag);
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
};

const applyTags = ({ pageTitle, desc, url, noIndex }) => {
  document.title = pageTitle;
  upsert("meta", 'meta[name="description"]', { name: "description", content: desc });
  upsert("link", 'link[rel="canonical"]', { rel: "canonical", href: url });
  upsert("meta", 'meta[property="og:url"]', { property: "og:url", content: url });
  upsert("meta", 'meta[property="og:title"]', { property: "og:title", content: pageTitle });
  upsert("meta", 'meta[property="og:description"]', { property: "og:description", content: desc });
  upsert("meta", 'meta[property="og:image"]', { property: "og:image", content: OG_IMAGE });
  upsert("meta", 'meta[property="og:image:secure_url"]', { property: "og:image:secure_url", content: OG_IMAGE });
  upsert("meta", 'meta[property="og:image:alt"]', { property: "og:image:alt", content: IMAGE_ALT });
  upsert("meta", 'meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
  upsert("meta", 'meta[name="twitter:title"]', { name: "twitter:title", content: pageTitle });
  upsert("meta", 'meta[name="twitter:description"]', { name: "twitter:description", content: desc });
  upsert("meta", 'meta[name="twitter:image"]', { name: "twitter:image", content: OG_IMAGE });
  upsert("meta", 'meta[name="twitter:image:alt"]', { name: "twitter:image:alt", content: IMAGE_ALT });
  upsert("meta", 'meta[name="robots"]', { name: "robots", content: noIndex ? "noindex, nofollow" : INDEXABLE });
  upsert("meta", 'meta[name="googlebot"]', { name: "googlebot", content: noIndex ? "noindex, nofollow" : "index, follow" });
};

const PageSEO = ({ title, description, path, noIndex }) => {
  const pageTitle = title ? `${title} | ${SITE}` : DEFAULT_TITLE;
  const desc = description || DEFAULT_DESC;
  const url = path ? `${BASE_URL}${path}` : BASE_URL;

  useEffect(() => {
    applyTags({ pageTitle, desc, url, noIndex });
    // Leaving a page that declares SEO (e.g. into the app shell) must not leave
    // its canonical behind.
    return () => applyTags({ pageTitle: DEFAULT_TITLE, desc: DEFAULT_DESC, url: BASE_URL, noIndex: false });
  }, [pageTitle, desc, url, noIndex]);

  return null;
};

export default PageSEO;
